/* Borderless size rails progressively enhance native scrolling.
 * Newly filtered/recommended cards upgrade automatically; no global observer.
 * Sizes remain informational, not an accidental quick-buy trigger.
 */
if (!customElements.get('fendo-size-rail')) {
  customElements.define('fendo-size-rail', class extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.size-pills');
      this.arrow = this.querySelector('.fendo-size-rail__arrow');
      if (!this.rail || !this.arrow || this.controller) return;

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.update = () => {
        // Include the arrow's reserved width when deciding whether it is needed.
        const availableWidth = this.clientWidth;
        const overflowing = this.rail.scrollWidth > availableWidth + 1;
        this.arrow.hidden = !overflowing;
        const atEnd = Math.abs(this.rail.scrollLeft) + this.rail.clientWidth >= this.rail.scrollWidth - 2;
        this.arrow.toggleAttribute('data-back', atEnd);
        this.arrow.setAttribute('aria-label', atEnd ? this.arrow.dataset.previousLabel : this.arrow.dataset.nextLabel);
      };
      this.arrow.addEventListener('click', () => {
        const backwards = this.arrow.hasAttribute('data-back');
        const rtl = getComputedStyle(this.rail).direction === 'rtl';
        this.rail.scrollBy({
          left: (backwards ? -1 : 1) * (rtl ? -1 : 1) * Math.max(38, this.rail.clientWidth - 38),
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
        });
      }, { signal });
      this.rail.addEventListener('scroll', this.update, { passive: true, signal });
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this);
      this.setAttribute('data-ready', '');
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
    }
  });
}

/* FENDO SmartSize bridge — idempotent, scoped to the main product section.
 * Hide the native app source only when an enabled replacement exists.
 * Option names and app settings are not changed by this bridge.
 */
(() => {
  if (window.__fendoSmartSizeBridgeInitialized) return;

  const sectionSelector = '.shopify-section--main-product';

  // This bridge only has work to do on a product template. Bailing out here keeps
  // its six global listeners off every other page of the store. The Theme Editor
  // still works, because the editor reloads the page when the template changes.
  const isProductPage = window.themeVariables?.settings?.pageType === 'product'
    || document.querySelector(sectionSelector) !== null;

  if (!isProductPage) return;

  window.__fendoSmartSizeBridgeInitialized = true;

  const records = new Map();
  const observedAttributes = ['class', 'style', 'disabled', 'hidden'];

  const setAttribute = (node, name, value) => {
    if (node && node.getAttribute(name) !== value) node.setAttribute(name, value);
  };
  const restoreAttribute = (node, name, value) => {
    if (!node) return;
    if (value === null) {
      if (node.hasAttribute(name)) node.removeAttribute(name);
    } else {
      setAttribute(node, name, value);
    }
  };
  const toggleClass = (node, name, enabled) => {
    if (node && node.classList.contains(name) !== enabled) node.classList.toggle(name, enabled);
  };

  const restoreNative = (record) => {
    const previous = record.native;
    if (!previous) return;
    toggleClass(previous.host, 'fendo-smartsize-host-hidden', false);
    toggleClass(previous.source, 'fendo-smartsize-source', false);
    restoreAttribute(previous.source, 'aria-hidden', previous.ariaHidden);
    restoreAttribute(previous.button, 'tabindex', previous.tabIndex);
    record.native = null;
  };

  const hideNative = (record, source, button) => {
    if (record.native?.source !== source || record.native?.button !== button) {
      restoreNative(record);
      record.native = {
        source, button,
        host: source.closest('.product-info__block-item'),
        ariaHidden: source.getAttribute('aria-hidden'),
        tabIndex: button.getAttribute('tabindex')
      };
    }
    toggleClass(record.native.host, 'fendo-smartsize-host-hidden', true);
    toggleClass(source, 'fendo-smartsize-source', true);
    setAttribute(source, 'aria-hidden', 'true');
    setAttribute(button, 'tabindex', '-1');
  };

  const appIsReady = (button) => Boolean(
    button && button.classList.contains('visible') && !button.disabled && !button.hidden
  );

  const setup = (record) => {
    if (!record.section.isConnected) return;
    // Do not feed our own DOM writes back into the observer.
    record.observer.disconnect();
    try {
      const triggers = Array.from(record.section.querySelectorAll('.fendo-smartsize-trigger'));
      const source = record.section.querySelector('#smartrecom-inline-button');
      const appButton = source?.querySelector('#smartrecom-button');
      const ready = triggers.length > 0 && appIsReady(appButton);

      for (const trigger of triggers) {
        toggleClass(trigger, 'is-smartsize-ready', ready);
        if (trigger.disabled === ready) trigger.disabled = !ready;
        if (!trigger.getAttribute('aria-label') && window.fendoStrings?.sizeGuideOpen) {
          setAttribute(trigger, 'aria-label', window.fendoStrings.sizeGuideOpen);
        }
      }
      if (ready) hideNative(record, source, appButton);
      else restoreNative(record);
    } finally {
      if (record.section.isConnected) {
        record.observer.observe(record.section, {
          childList: true, subtree: true, attributes: true, attributeFilter: observedAttributes
        });
      }
    }
  };

  const schedule = (record) => {
    if (record.frame !== null) return;
    record.frame = requestAnimationFrame(() => {
      record.frame = null;
      setup(record);
    });
  };

  const dispose = (section) => {
    const record = records.get(section);
    if (!record) return;
    record.observer.disconnect();
    if (record.frame !== null) cancelAnimationFrame(record.frame);
    section.removeEventListener('click', record.onClick);
    restoreNative(record);
    records.delete(section);
  };

  const initialize = (section) => {
    if (records.has(section)) {
      schedule(records.get(section));
      return;
    }
    const record = { section, observer: null, frame: null, native: null, onClick: null };
    record.observer = new MutationObserver((mutations) => {
      // Variant rerenders replace children. Readiness is owned by the app itself.
      if (mutations.some((mutation) => mutation.type === 'childList' ||
        mutation.target.matches?.('#smartrecom-inline-button, #smartrecom-button'))) {
        schedule(record);
      }
    });
    record.onClick = (event) => {
      const trigger = event.target.closest?.('.fendo-smartsize-trigger');
      if (!trigger || !section.contains(trigger)) return;
      event.preventDefault();
      event.stopPropagation();
      const button = section.querySelector('#smartrecom-inline-button #smartrecom-button');
      if (!trigger.disabled && appIsReady(button)) button.click();
      else schedule(record);
    };
    records.set(section, record);
    section.addEventListener('click', record.onClick);
    setup(record);
  };

  const refresh = () => {
    for (const section of records.keys()) if (!section.isConnected) dispose(section);
    document.querySelectorAll(sectionSelector).forEach(initialize);
  };
  document.addEventListener('product:rerender', refresh, true);
  document.addEventListener('variant:change', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const section of records.keys()) {
      if (event.target === section || event.target.contains?.(section)) dispose(section);
    }
  });
  window.addEventListener('pageshow', refresh);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();

/* Shopify Forms renders inside an open shadow root, outside theme CSS. */
(() => {
  const observed = new WeakSet();
  const pendingHosts = new WeakMap();
  const MAX_ROOT_ATTEMPTS = 40;

  const connect = (host, attempt = 0) => {
    if (!host.isConnected) return;
    const root = host.shadowRoot;
    if (!root) {
      // Definition and shadow-root readiness need not happen in the same frame.
      // Retry only this embed, for at most ten seconds; never poll the full page.
      if (attempt < MAX_ROOT_ATTEMPTS && !pendingHosts.has(host)) {
        pendingHosts.set(host, window.setTimeout(() => {
          pendingHosts.delete(host);
          connect(host, attempt + 1);
        }, 250));
      }
      return;
    }
    if (pendingHosts.has(host)) {
      window.clearTimeout(pendingHosts.get(host));
      pendingHosts.delete(host);
    }

    const resizeHeading = () => {
      root
        .querySelectorAll('section[data-sizing="form-wrapper"] h2')
        .forEach((heading) => {
          heading.style.setProperty('font-size', '18px', 'important');
          heading.style.setProperty('line-height', '1.25', 'important');
        });
    };

    resizeHeading();

    if (observed.has(root)) return;
    observed.add(root);

    new MutationObserver(resizeHeading).observe(root, {
      childList: true,
      subtree: true
    });
  };

  const scan = () => {
    document.querySelectorAll('shopify-forms-embed').forEach((host) => connect(host));
  };

  // One scan per frame at most. The previous version re-queried every added node
  // and re-scanned the whole document after every click in the page.
  let scanScheduled = false;

  const scheduleScan = () => {
    if (scanScheduled) return;
    scanScheduled = true;

    requestAnimationFrame(() => {
      scanScheduled = false;
      scan();
    });
  };

  scan();

  // Forms are injected by the app, and a popup form can appear at any time, so the
  // observer stays. It is scoped to <body> and only reacts when an embed is added.
  const hasEmbed = (node) => node instanceof Element &&
    (node.localName === 'shopify-forms-embed' || node.querySelector('shopify-forms-embed') !== null);

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (hasEmbed(node)) {
          scheduleScan();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // The custom element usually upgrades after its markup is inserted; that is the
  // moment the shadow root becomes readable.
  customElements.whenDefined('shopify-forms-embed').then(scheduleScan);
})();

/* Moved here from snippets/fendo-body-end.liquid (was inline on every page).
 * FENDO: first actual display, not only dismissal; Shopify Forms 1099196. */
(() => {
  'use strict';
  if (window.Shopify && window.Shopify.designMode) return;

  const hostId = 'app-embed-container-1099196';
  const key = 'fendo:popup:1099196:shown:v1';
  const stores = ['localStorage', 'sessionStorage'];
  const seenBefore = stores.some((name) => {
    try { return window[name].getItem(key) === '1'; } catch (_) { return false; }
  });
  let pageObserver;
  let formObserver;
  let timer;
  let attempts = 0;
  let stopped = false;
  let observedRoot;

  // Hide only this popup on return visits. Its own Close handler releases
  // the overlay, focus trap and scroll lock; do not edit Shopify's storage.
  if (seenBefore) {
    const style = document.createElement('style');
    style.id = 'fendo-popup-once-1099196';
    style.textContent = 'shopify-forms-embed#' + hostId + '{visibility:hidden!important;pointer-events:none!important}';
    document.head.appendChild(style);
  }

  function stop() {
    stopped = true;
    if (pageObserver) pageObserver.disconnect();
    if (formObserver) formObserver.disconnect();
    if (timer) clearTimeout(timer);
    window.removeEventListener('pagehide', stop);
  }

  function inspect() {
    if (stopped) return;
    const host = document.getElementById(hostId);
    const root = host && host.shadowRoot;
    if (!root) return;
    if (root !== observedRoot) {
      if (formObserver) formObserver.disconnect();
      observedRoot = root;
      formObserver = new MutationObserver(inspect);
      formObserver.observe(root, { childList: true, subtree: true, attributes: true });
      formObserver.observe(host, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      pageObserver.disconnect();
    }
    const app = root.querySelector('[data-layout="overlay"][data-current-step="form"]');
    const dialog = app && app.querySelector('[role="dialog"]');
    const close = dialog && dialog.querySelector('[aria-label="Close modal"]');
    if (!dialog || !close) return;
    const rect = dialog.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0 || getComputedStyle(host).display === 'none') return;

    if (seenBefore) {
      close.click();
      stop();
      return;
    }
    // Never mark an invisible mount, and never close the first impression.
    if (getComputedStyle(host).visibility === 'hidden' || getComputedStyle(dialog).visibility === 'hidden') return;
    for (const name of stores) {
      try { window[name].setItem(key, '1'); } catch (_) { /* Storage may be blocked. */ }
    }
    stop();
  }

  // A shadow root can be attached without a document mutation. Poll only
  // during startup, then rely on the scoped observer (no permanent interval).
  function findRoot() {
    inspect();
    if (stopped || observedRoot) return;
    if (++attempts < 240) timer = setTimeout(findRoot, 250);
    else stop();
  }
  pageObserver = new MutationObserver(inspect);
  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
  window.addEventListener('pagehide', stop, { once: true });
  findRoot();
})();
